import moment from 'moment';

import { PROVIDER_TYPES, DROP_OFF_PROGRAMS } from '../config/service';
import {
  getGoLockerLocations,
  getFedexLocations,
  getFedexGroundLocations,
  getTheReturnLocations,
  getAmazonShippingLocations,
  fetchShippingProviderLocations,
} from './service';
import { getAddressString } from './locations';
import { withUnknownAvailability } from './goLocker';

/**
 * Shared drop-off location resolution for the shipping-label surfaces.
 *
 * Lifted out of InstantLabelsView so the Regular Labels cards can resolve the
 * same nearest-location list the Instant screen already showed on tap — there
 * must be one definition of "where can I drop this off", or the two screens can
 * disagree about the same label.
 */

// Carrier location APIs (esp. FedEx via the emulator) can hang; bound them so a
// caller never spins forever — a timeout falls back to the empty/error state.
export const withTimeout = (promise, ms) => {
  let timer;
  const timeout = new Promise((_, reject) => {
    timer = setTimeout(() => reject(new Error('drop-off-timeout')), ms);
  });
  return Promise.race([promise, timeout]).finally(() => clearTimeout(timer));
};

/**
 * Resolve a provider's drop-off locations exactly like the /register flow's
 * ShippingProviderLocations: BE-attached nearest locations win; otherwise fetch
 * per provider type (FedEx / The Return / URL-backed config).
 */
export const resolveDropOffLocations = async ({
  provider,
  locations,
  address,
  presetLocations,
  dropOffProgram,
}) => {
  // GoLocker FIRST — it rides on THE_RETURN's provider id, so the map below would
  // send it to /the-return (dry cleaners). Its own endpoint returns the banks WITH
  // live availability, which is the whole point of the block, so it is always
  // fetched: a preset list has no availability and GoLocker ships no
  // `locations.enabled` config. On failure the preset banks stay, with availability
  // UNKNOWN — the same fallback as the shipping-step panel (GoLockerLiveLocations) —
  // and only a failure with nothing to fall back on throws.
  if (dropOffProgram === DROP_OFF_PROGRAMS.GOLOCKER) {
    const { data, error } = await getGoLockerLocations({ address: address ?? {} });
    if (error && Array.isArray(presetLocations) && presetLocations.length) {
      console.warn('[resolveDropOffLocations] /golocker failed; showing preset banks:', error);
      return withUnknownAvailability(presetLocations);
    }
    if (error) throw new Error(error?.message ?? error);
    return Array.isArray(data?.locations) ? data.locations : [];
  }
  if (Array.isArray(presetLocations) && presetLocations.length) return presetLocations;
  if (!locations?.enabled) return [];
  // These services resolve to { data, error } and never throw, so an unchecked
  // `error` would surface as an empty location list (a false "no spots near you").
  // Throw on a real error so the caller can distinguish it from a genuine empty.
  // `error` is checkResponseError's `{ code, message }`; its message is what the
  // register flow shows inline, so it must survive the throw (not "[object Object]").
  // One entry per provider that has a dedicated endpoint. A map rather than a chain of
  // identical if-blocks: the sixth provider is a row, and there is no way for one
  // provider's branch to drift from the others' error handling.
  const fetchLocations = {
    [PROVIDER_TYPES.FEDEX]: getFedexLocations,
    [PROVIDER_TYPES.FEDEX_GROUND]: getFedexGroundLocations,
    [PROVIDER_TYPES.THE_RETURN]: getTheReturnLocations,
    [PROVIDER_TYPES.AMAZON_SHIPPING]: getAmazonShippingLocations,
  }[provider];

  if (fetchLocations) {
    const { data, error } = await fetchLocations({
      address: address ?? {},
      distance: locations.distance,
    });
    if (error) throw new Error(error?.message ?? error);
    return data?.locations ?? [];
  }

  const { data, error } = await fetchShippingProviderLocations({
    ...locations,
    address: getAddressString(address),
    zipcode: address?.zip,
  });
  if (error) throw new Error(error?.message ?? error);
  return Array.isArray(data) ? data : [];
};

/**
 * "387 New York Ave, Brooklyn, NY" — the street line with city and state, which
 * every drop-off provider sends on `address` (FedEx, The Return, Amazon Shipping).
 * City/state are dropped when missing; no street means no line at all, so callers
 * keep their existing fallback (the store name).
 */
export const getLocationAddressLine = (address) => {
  const street = address?.address;
  if (!street) return '';
  const cityState = [address.city, address.state].filter(Boolean).join(', ');
  return cityState ? `${street}, ${cityState}` : street;
};

// "8p" / "8:30p" — the design drops the "m" from am/pm and the ":00" from a
// whole hour (Figma 10818-10360).
const formatClosingTime = (closingTime) => {
  const time = moment(closingTime);
  const meridiem = time.format('a').charAt(0);
  return `${time.minutes() ? time.format('h:mm') : time.format('h')}${meridiem}`;
};

/**
 * Whether an API `dayOfWeek` is today. The API numbers days 1 = Monday ..
 * 7 = Sunday; moment's `day()` is 0 = Sunday .. 6 = Saturday, so Sunday only
 * matches after `% 7`. `day()`, not the locale-relative `weekday()`.
 */
export const isTodayDayOfWeek = (dayOfWeek) => moment().day() === Number(dayOfWeek) % 7;

/**
 * "Open · Closes at 8p" for a location that is still open today, "Closed"
 * otherwise. Reads the schedule the same way DropOffSheet does.
 *
 * Returns '' when the location ships no opening hours at all, so callers render
 * nothing rather than a misleading "Closed" for a store we simply know nothing
 * about.
 */
export const getTodayOpenLabel = (openingHours) => {
  if (!Array.isArray(openingHours) || !openingHours.length) return '';
  const today = openingHours.find(
    (h) => isTodayDayOfWeek(h.dayOfWeek) && moment().isBefore(h.closingTime),
  );
  if (!today) return 'Closed';
  return `Open · Closes at ${formatClosingTime(today.closingTime)}`;
};

/**
 * "1.7 miles" — one decimal (Figma 10818-10358). Deliberately not pluralized:
 * the value always carries a decimal, and "1.0 mile" reads like a formatting
 * bug where "1.0 miles" reads like the unit it is.
 *
 * Returns '' for a missing or unparseable distance so the caller can drop the
 * separator along with it.
 */
export const getDistanceLabel = (distance) => {
  const miles = parseFloat(distance);
  if (Number.isNaN(miles)) return '';
  return `${miles.toFixed(1)} miles`;
};
